import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../../../styles/pages/submissionTracker.css";
import apiClient from "../../../shared/API/apiClient.jsx";
import { useReportingYear } from "../../../context/ReportingYearContext";
import { getStoredRole } from "../../../shared/utils/auth";
import Toast from "../../../shared/components/Toast";

// ── FA icon map — converts API icon keys → real Font Awesome classes ────────
const FA_ICON_MAP = {
  "fa-draft":       "fa-solid fa-pen-to-square",
  "fa-submitted":   "fa-solid fa-paper-plane",
  "fa-reviewed":    "fa-solid fa-circle-check",
  "fa-approved":    "fa-solid fa-check-double",
  "fa-locked":      "fa-solid fa-lock",
  "fa-lock":        "fa-solid fa-lock",
  "fa-notstarted":  "fa-solid fa-clock",
  "fa-rework":      "fa-solid fa-rotate-left",
  "fa-list":        "fa-solid fa-list",
  "fa-exclamation": "fa-solid fa-circle-exclamation",
  "fa-paperclip":   "fa-solid fa-paperclip",
  "fa-view":        "fa-solid fa-eye",
  "fa-info-circle": "fa-solid fa-circle-info",
};

const resolveIcon  = (key) => `fa-solid ${key || "fa-circle"}`;

// ── Sub-components ─────────────────────────────────────────────────────────

const StatusBadge = ({ row }) => (
  <span
    className="u-badge"
    style={{
      "--badge-bg": row.color_code || "#f1f5f9",
      "--badge-fg": row.fore_color || "#334155",
    }}
  >
    {/* API field is "icon", not "icon_name" */}
    <i className={`${resolveIcon(row.icon)} u-badge__icon`} />
    {/* API field is "display_status_name" for the label */}
    {row.display_status_name || row.status || "—"}
  </span>
);

const ActionCell = ({ row, onLock, lockingId, canEdit }) => {
  const isLocked  = row.lock_status?.toLowerCase() === "locked";
  const isLocking = lockingId === row.submission_id;

  if (isLocked)         return <span className="u-action-muted">Permanently locked</span>;
  if (!row.approved_by) return <span className="u-action-muted">Pending approval</span>;
  if (!canEdit)         return <span className="u-action-muted">View only</span>;

  return (
    <button className="btn-lock-now" onClick={() => onLock(row.submission_id)} disabled={isLocking}>
      {isLocking ? "Locking…" : "🔒 Lock Now"}
    </button>
  );
};

const ActionButton = ({ row, reportingYearId, reportingYear, selectedEntity,selectedEntityName }) => {
  if (!row.action_name) return null;
  return (
    <Link
      to={row.action_url}
      className="action-link"
      state={{
        principle_id:  row.principle_id,
        submission_id: row.submission_id,
        section_name:  row.principle_name,
        reportingYearId,
        reportingYear,
        selectedEntity,
        entityName: selectedEntityName,
        sourcePage: "approval-screen"
      }}
    >
      {row.action_name} →
    </Link>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const ApprovalLock = () => {
  const { reportingYear, reportingYearId } = useReportingYear();
  const location    = useLocation();
  const passedState = location.state || {};
  const navigate    = useNavigate();

  const [selectedEntity, setSelectedEntity] = useState(passedState.selectedEntity || "");
  const [legalEntities,  setLegalEntities]  = useState(passedState.legalEntities  || []);
  const [sections,       setSections]       = useState([]);
  const [statusData,     setStatusData]     = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [lockingId,      setLockingId]      = useState(null);
  const [toast,          setToast]          = useState(null);
  const [isYearLocked,   setIsYearLocked]   = useState(true);

  const role       = getStoredRole();
  const canApprove = ["Admin", "Super Admin", "Approver"].includes(role);
  const canEdit    = canApprove && !isYearLocked;
const selectedEntityName = legalEntities.find(
    (e) => e.entity_id == selectedEntity
  )?.entity_name;

  useEffect(() => {
    if (!reportingYear) return;
    apiClient.post("/api/reportingyear")
      .then((res) => {
        const years = res.data?.data ?? [];
        const norm  = (v) => (v ?? "").toString().trim().replace(/^fy/i, "").toLowerCase();
        const match = years.find((y) => norm(y.year_label) === norm(reportingYear));
        setIsYearLocked(match ? match.is_locked === 1 : true);
      })
      .catch(() => setIsYearLocked(true));
  }, [reportingYear]);

  useEffect(() => {
    if (passedState.legalEntities?.length > 0) return;
    if (!reportingYearId) return;
    apiClient
      .get("/api/legal-entities-by-year", { params: { reporting_year_id: reportingYearId } })
      .then((res) => setLegalEntities(res.data.code === 200 ? res.data.data : []))
      .catch(() => setLegalEntities([]));
  }, [reportingYearId, passedState.legalEntities]);

  useEffect(() => {
    if (!reportingYearId || !selectedEntity) return;
    setLoading(true);
    const params = { reporting_year_id: reportingYearId, legal_entity_id: selectedEntity };
    Promise.all([
      apiClient.get("/api/approval-list",   { params }),
      apiClient.get("/api/approval-status", { params }),
    ])
      .then(([listRes, statusRes]) => {
        if (listRes.data.success)   setSections(listRes.data.data     ?? []);
        if (statusRes.data.success) setStatusData(statusRes.data.data ?? []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [reportingYearId, selectedEntity]);

  const lockSection = async (id) => {
    if (!canEdit) return;
    setLockingId(id);
    try {
      const res = await apiClient.post("/api/brsr/lock-submission", { id, reporting_year: reportingYear });
      if (res.data.status === "success") {
        setSections((prev) => prev.map((s) => s.submission_id === id ? { ...s, lock_status: "Locked" } : s));
        setToast({ message: "Section locked successfully.", type: "success" });
      }
    } catch { setToast({ message: "Failed to lock", type: "error" }); }
    finally { setLockingId(null); }
  };

  return (
    <div className="dash-page">
      <div className="dash-page-title">Approval &amp; Lock Screen</div>
      <div className="dash-page-subtitle">Lock approved sections to prevent edits</div>

      {/* Filter card */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>
          <input className="filter-input" value={reportingYear || ""} disabled />
        </div>
        <div className="filter-group">
          <label>Legal Entity *</label>
          <select className="filter-input" value={selectedEntity} onChange={(e) => setSelectedEntity(e.target.value)}>
            <option value="">Select</option>
            {legalEntities.map((e) => (
              <option key={e.entity_id} value={e.entity_id}>{e.entity_name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Empty state */}
      {!selectedEntity && (
        <div className="empty-entity">
          <div className="empty-entity__icon"><i className="fa-solid fa-building" /></div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
            Choose a legal entity from the filter above to view approval and lock status.
          </div>
        </div>
      )}

      {/* Content */}
      {selectedEntity && (
        <>
          {/* Stat cards */}
          <div className="stats-row">
            {statusData.map((item, idx) => (

              <div key={idx} className="stat-card" style={{ "--card-border": "transparent", cursor: "default" }}>
                <div className="stat-icon" style={{ background: item.color_code || "#f1f5f9", color: item.fore_color || "#334155" }}>
                  {/* API field is "icon" */}
                  <i className={resolveIcon(item.icon)} />

                </div>
                <div className="stat-body">
                  {/* API field is "display_heading" or "display_status_name" */}
                  <span className="stat-title">
                    {item.display_heading || item.display_status_name || item.status_name}
                  </span>
                  <span className="stat-value">{item.count}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="table-card">
          <div className="info_text">
             
              Showing all <strong>Reviewed, Approved, and Locked</strong>  Principles.
            </div>
            {loading ? (
              <div className="inline-state"><div className="spinner" /><span>Loading data…</span></div>
            ) : (
              <table className="u-table">
                <thead>
                  <tr>
                    <th>PRINCIPLE</th>
                    <th>DESCRIPTION</th>
                    <th>REVIEWED BY</th>
                    <th>REVIEWED ON</th>
                    <th>STATUS</th>
                    <th>APPROVED BY</th>
                    <th>APPROVED ON</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.length === 0 ? (
                    <tr><td colSpan={8} className="u-table-empty">No records found for this entity.</td></tr>
                  ) : (
                    sections.map((row) => (
                      <tr key={row.submission_id}>
                        <td className="td-code">{row.principle_code}</td>
                        <td className="question-text">{row.principle_name}</td>
                        <td className="td-muted">{row.reviewed_by   ?? "—"}</td>
                        <td className="td-date">{row.reviewed_date  || "—"}</td>
                        <td><StatusBadge row={row} /></td>
                        <td className="td-muted">{row.approved_by   ?? "—"}</td>
                        <td className="td-date">{row.approved_date  || "—"}</td>
                        <td>
                         <div className="action">
                          {row.action_name ? (
                            <ActionButton row={row} reportingYearId={reportingYearId} reportingYear={reportingYear} selectedEntity={selectedEntity} selectedEntityName={selectedEntityName} />
                          ) : (
                            <ActionCell row={row} onLock={lockSection} lockingId={lockingId} canEdit={canEdit} />
                          )}
</div>
                          
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="ecf-footer">
            <button className="back-btn" onClick={() => navigate("/dashboard/review-screen", { state: { selectedEntity } })}>
              ← Back to Review
            </button>
          </div>
        </>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ApprovalLock;
